export const SUBSCRIPTION_STATUSES = ['active', 'pending', 'expired', 'canceled'] as const;

export type SubscriptionStatusCode = (typeof SUBSCRIPTION_STATUSES)[number];

/** Map API / legacy translated labels → canonical status codes */
const LABEL_TO_CODE: Record<string, SubscriptionStatusCode> = {
  active: 'active',
  pending: 'pending',
  expired: 'expired',
  canceled: 'canceled',
  // Arabic (backend enums)
  'الاشتراك نشط': 'active',
  'قيد الانتظار': 'pending',
  'الاشتراك منتهي': 'expired',
  'تم إلغاء الاشتراك': 'canceled',
  // Arabic (short UI)
  نشط: 'active',
  معلق: 'pending',
  منتهي: 'expired',
  ملغي: 'canceled',
  // English
  'Active Subscription': 'active',
  'Pending Subscription': 'pending',
  'Subscription Expired': 'expired',
  'Subscription Canceled': 'canceled',
  Active: 'active',
  Pending: 'pending',
  Expired: 'expired',
  Canceled: 'canceled',
  // German
  'Aktives Abonnement': 'active',
  'Ausstehendes Abonnement': 'pending',
  'Abonnement abgelaufen': 'expired',
  'Abonnement storniert': 'canceled',
};

const AR_LABELS: Record<SubscriptionStatusCode, string> = {
  active: 'نشط',
  pending: 'معلق',
  expired: 'منتهي',
  canceled: 'ملغي',
};

export function isSubscriptionStatusCode(value: string): value is SubscriptionStatusCode {
  return (SUBSCRIPTION_STATUSES as readonly string[]).includes(value);
}

/**
 * Normalize any status string from the API into a select-friendly code.
 */
export function normalizeSubscriptionStatus(
  value: string | null | undefined
): SubscriptionStatusCode {
  if (!value) {
    return 'pending';
  }

  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();

  if (isSubscriptionStatusCode(lower)) {
    return lower;
  }

  if (trimmed.startsWith('enums.subscription_status.')) {
    const code = trimmed.slice('enums.subscription_status.'.length).toLowerCase();
    if (isSubscriptionStatusCode(code)) {
      return code;
    }
  }

  if (LABEL_TO_CODE[trimmed]) {
    return LABEL_TO_CODE[trimmed];
  }

  const caseInsensitive = Object.entries(LABEL_TO_CODE).find(
    ([label]) => label.toLowerCase() === lower
  );
  if (caseInsensitive) {
    return caseInsensitive[1];
  }

  return 'pending';
}

export function getSubscriptionStatusLabelAr(code: SubscriptionStatusCode): string {
  return AR_LABELS[code];
}

export function normalizeSubscriptionRecord<T extends { status?: string | null; status_label?: string | null }>(
  record: T
): T & { status: SubscriptionStatusCode; status_label: string } {
  const status = normalizeSubscriptionStatus(record.status);
  return {
    ...record,
    status,
    status_label: record.status_label || getSubscriptionStatusLabelAr(status),
  };
}

/** Unwrap Laravel resource collection payloads: { data: [...] } or plain array */
export function unwrapSubscriptionList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown[] }).data)) {
    return (payload as { data: unknown[] }).data;
  }
  return [];
}
